import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ 
    items: [], 
    subtotal: 0, 
    bundle_discounts: [], 
    total_savings: 0, 
    total: 0 
  });
  const [loading, setLoading] = useState(false);
  const [localCart, setLocalCart] = useState([]);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      // Use local storage for guest cart
      try {
        const stored = localStorage.getItem('guest_cart');
        if (stored) {
          setLocalCart(JSON.parse(stored));
        }
      } catch {
        // Invalid stored cart data
        localStorage.removeItem('guest_cart');
      }
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch {
      // Failed to fetch cart - will use empty cart
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1, variantId = null) => {
    if (!isAuthenticated) {
      // Add to local cart for guests
      const cartKey = `${productId}:${variantId || ''}`;
      setLocalCart(prevCart => {
        const existing = prevCart.find(item => 
          `${item.product_id}:${item.variant_id || ''}` === cartKey
        );
        let newCart;
        if (existing) {
          newCart = prevCart.map(item =>
            `${item.product_id}:${item.variant_id || ''}` === cartKey
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          const cartItem = { product_id: productId, quantity };
          if (variantId) cartItem.variant_id = variantId;
          newCart = [...prevCart, cartItem];
        }
        localStorage.setItem('guest_cart', JSON.stringify(newCart));
        return newCart;
      });
      return { success: true, requiresLogin: true };
    }

    try {
      const payload = { product_id: productId, quantity };
      if (variantId) payload.variant_id = variantId;
      const response = await axios.post(`${API}/cart/add`, payload);
      setCart(response.data);
      return { success: true };
    } catch (error) {
      throw error;
    }
  }, [isAuthenticated]);

  const updateQuantity = useCallback(async (productId, quantity, variantId = null) => {
    if (!isAuthenticated) return;

    try {
      const payload = { product_id: productId, quantity };
      if (variantId) payload.variant_id = variantId;
      const response = await axios.put(`${API}/cart/update`, payload);
      setCart(response.data);
    } catch (error) {
      throw error;
    }
  }, [isAuthenticated]);

  const removeFromCart = useCallback(async (productId, variantId = null) => {
    if (!isAuthenticated) {
      setLocalCart(prevCart => {
        const newCart = prevCart.filter(item => {
          if (variantId) {
            return !(item.product_id === productId && item.variant_id === variantId);
          }
          return item.product_id !== productId;
        });
        localStorage.setItem('guest_cart', JSON.stringify(newCart));
        return newCart;
      });
      return;
    }

    try {
      // For now, remove by product_id - backend needs update for variant support
      const response = await axios.delete(`${API}/cart/remove/${productId}`);
      setCart(response.data);
    } catch (error) {
      throw error;
    }
  }, [isAuthenticated]);

  const clearCart = useCallback(async () => {
    if (!isAuthenticated) {
      setLocalCart([]);
      localStorage.removeItem('guest_cart');
      return;
    }

    try {
      const response = await axios.delete(`${API}/cart/clear`);
      setCart(response.data);
    } catch (error) {
      throw error;
    }
  }, [isAuthenticated]);

  const syncGuestCart = useCallback(async () => {
    // Sync local guest cart to server after login
    setLocalCart(prevLocalCart => {
      if (prevLocalCart.length > 0) {
        // Perform sync in background
        (async () => {
          for (const item of prevLocalCart) {
            try {
              await axios.post(`${API}/cart/add`, item);
            } catch {
              // Failed to sync item - continue with others
            }
          }
          localStorage.removeItem('guest_cart');
          fetchCart();
        })();
      }
      return [];
    });
  }, [fetchCart]);

  const cartItemCount = isAuthenticated 
    ? cart.items.reduce((sum, item) => sum + item.quantity, 0)
    : localCart.reduce((sum, item) => sum + item.quantity, 0);

  const cartTotal = isAuthenticated ? cart.total : 0;

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    cart,
    localCart,
    loading,
    cartItemCount,
    cartTotal,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart,
    syncGuestCart
  }), [cart, localCart, loading, cartItemCount, cartTotal, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, syncGuestCart]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
